import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {interval, ReplaySubject} from 'rxjs';
import {UserPermissions} from '../../../../shared/interface/user-permissions';
import {InsuranceCompany} from '../../../../shared/interface/insurance-company';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {AuthService} from '../../../../shared/services/auth.service';
import {FilterService} from '../../../../shared/services/filter.service';
import {ActivatedRoute, Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {StoreRootState} from '../../../../shared/ngrx/reducers';
import {delay, distinctUntilChanged, takeUntil, tap} from 'rxjs/operators';
import {CountedAndPaginated} from '../../../../shared/interface/response/counted-and-paginated';
import {getCurrentRouteState} from '../../../../shared/ngrx/selector';
import {RouterStateUrl} from '../../../../shared/ngrx/route-serializer';
import {Uuid} from '../../../../shared/interface/response/uuid';
import {JobResultDialogComponent} from '../../../../shared/components/job-result-dialog/job-result-dialog.component';
import {HttpErrorResponse} from '@angular/common/http';
import {RegistrationIdImport} from '../../../../shared/interface/registration-id-import';
import {CommissionRedirectionImport} from '../../../../shared/interface/commission-redirection-import';
import {CommissionRedirectionService} from '../../../../shared/services/commission-redirection.service';
import {ImportResultDialogComponent} from '../import-result-dialog/import-result-dialog.component';
import {CommissionRedirection} from '../../../../shared/interface/commission-redirection';
import {ConfirmDialogComponent} from '../../../../shared/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-all-redirection-imports-list',
  templateUrl: './all-redirection-imports-list.component.html',
  styleUrls: ['./all-redirection-imports-list.component.scss']
})
export class AllRedirectionImportsListComponent implements AfterViewInit, OnInit, OnDestroy {

  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  initialFilters: any;
  imports: CommissionRedirectionImport[] = [];
  displayedColumns: string[] = [
    'name', 'company', 'insuranceCompany', 'createdAt', 'createdBy', 'totalRows', 'status', 'actions'
  ];
  totalData = 0;
  userPermissions: ReplaySubject<UserPermissions>;
  selectedInsuranceCompanies: InsuranceCompany[] = [];

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private commissionRedirectionService: CommissionRedirectionService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private auth: AuthService,
    private filterService: FilterService,
    private activatedRoute: ActivatedRoute,
    private store: Store<StoreRootState>,
    private router: Router,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.commissionRedirectionService.importListSubject
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<CommissionRedirectionImport>) => {
          this.imports = e.items;
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      contains$name: 'string',
      company: 'int',
      in$insuranceCompany: 'int[]',
      gte$createdAt: 'string',
      lte$createdAt: 'string',
      createdBy: 'string',
      status: 'int',
    }, filters);
  }

  ngOnInit(): void {
    interval(5000)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        if (this.paginator.pageIndex === 0) {
          this.loadListData();
        }
      });
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: event.pageIndex,
        pageSize: event.pageSize,
        ...this.filtersForm.value
      })]);
    });

    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((state: RouterStateUrl) => {
        this.parseFilters(state.params);
        this.paginator.pageIndex = state.params.page;
        this.paginator.pageSize = state.params.pageSize;
        this.loadListData();
      })
    ;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  loadListData(): void {
    this.commissionRedirectionService.loadImportList(this.paginator.pageIndex, this.paginator.pageSize, this.initialFilters);
  }

  filter(): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      page: 0,
      pageSize: this.paginator.pageSize,
      ...this.filtersForm.value,
    })]);
  }

  downloadReport(): void {
    this.commissionRedirectionService.downloadImportsReport(this.initialFilters)
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }

  onImgError($event: ErrorEvent): void {
    // @ts-ignore
    $event.target.src = '/assets/avatar.png';
  }

  downloadImportFile(registrationIdImport: RegistrationIdImport): void {
    this.commissionRedirectionService.downloadImportFile(registrationIdImport.uuid);
  }

  trackTask(index: number, commissionRedirectionImport: CommissionRedirectionImport): string {
    return commissionRedirectionImport.uuid;
  }

  showResult(commissionRedirectionImport: CommissionRedirectionImport): void {
    this.dialog.open(ImportResultDialogComponent, {
      width: '500px',
      maxHeight: '80vh',
      data: {import: commissionRedirectionImport},
    });
  }

  downloadReportFile(commissionRedirectionImport: CommissionRedirectionImport): void {
    this.commissionRedirectionService.downloadReport({import: commissionRedirectionImport.uuid})
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  delete(commissionRedirectionImport: CommissionRedirectionImport): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć import przepięć?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.commissionRedirectionService.deleteImport(commissionRedirectionImport.uuid)
          .subscribe({
            next: () => {
              this.snack.open('Pomyślnie usunięto', '', {duration: 2000});
              this.loadListData();
            },
            error: (response: HttpErrorResponse) => {
              this.snack.open(response.error.error.text, response.status.toString(), {duration: 2000});
            }
          });
      }
    });
  }
}
